// src/components/DataViewer/JsonTreeViewer/JsonTreeViewer.tsx
// Fully recursive tree viewer that shows all nested levels
import React, { useState } from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ChevronRight as CollapseIcon,
} from '@mui/icons-material';

interface JsonTreeViewerProps {
  data: any;
  level?: number;
  parentKey?: string;
}

const JsonTreeViewer: React.FC<JsonTreeViewerProps> = ({
  data,
  level = 0,
  parentKey = 'root'
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpand = (key: string) => {
    setExpanded(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getValueType = (value: any): string => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };

  const getValueColor = (type: string): string => {
    switch (type) {
      case 'string': return '#98c379';
      case 'number': return '#d19a66';
      case 'boolean': return '#56b6c2';
      case 'null': return '#e06c75';
      case 'array': return '#c678dd';
      case 'object': return '#61afef';
      default: return '#abb2bf';
    }
  };

  const formatValue = (value: any, type: string): string => {
    if (type === 'string') return `"${value}"`;
    if (type === 'null') return 'null';
    if (type === 'boolean') return value.toString();
    if (type === 'number') return value.toString();
    return '';
  };

  const getCollectionSize = (value: any): string => {
    if (Array.isArray(value)) return `[${value.length}]`;
    if (typeof value === 'object' && value !== null) {
      return `{${Object.keys(value).length}}`;
    }
    return '';
  };

  const renderValue = (key: string, value: any, itemLevel: number) => {
    const valueType = getValueType(value);
    const isExpandable = valueType === 'object' || valueType === 'array';
    const uniqueKey = `${parentKey}.${key}.${itemLevel}`;
    const isExpanded = expanded[uniqueKey] ?? false;

    return (
      <Box key={uniqueKey} sx={{ mb: 0.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: itemLevel * 2,
            py: 0.25,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 1
            }
          }}
        >
          {isExpandable && (
            <IconButton
              size="small"
              onClick={() => toggleExpand(uniqueKey)}
              sx={{
                p: 0,
                mr: 0.5,
                width: 20,
                height: 20,
                color: '#64748B'
              }}
            >
              {isExpanded ? (
                <ExpandIcon sx={{ fontSize: 16, color: '#64748B' }} />
              ) : (
                <CollapseIcon sx={{ fontSize: 16, color: '#64748B' }} />
              )}
            </IconButton>
          )}

          {!isExpandable && (
            <Box sx={{ width: 20, mr: 0.5 }} />
          )}

          <Typography
            component="span"
            sx={{
              color: '#e06c75',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          >
            {key}:
          </Typography>

          {isExpandable && (
            <>
              <Typography
                component="span"
                sx={{
                  ml: 1,
                  color: getValueColor(valueType),
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }}
              >
                {valueType === 'array' ? '[' : '{'}
              </Typography>
              <Chip
                label={getCollectionSize(value)}
                size="small"
                sx={{
                  ml: 0.5,
                  height: 18,
                  fontSize: '0.7rem',
                  bgcolor: '#E2E8F0',
                  color: '#1E293B',
                  fontWeight: 700,
                  '& .MuiChip-label': {
                    color: '#1E293B',
                    px: 1
                  }
                }}
              />
              {!isExpanded && (
                <Typography
                  component="span"
                  sx={{
                    ml: 0.5,
                    color: getValueColor(valueType),
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }}
                >
                  {valueType === 'array' ? ']' : '}'}
                </Typography>
              )}
            </>
          )}

          {!isExpandable && (
            <Typography
              component="span"
              sx={{
                ml: 1,
                color: getValueColor(valueType),
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}
            >
              {formatValue(value, valueType)}
            </Typography>
          )}
        </Box>

        {isExpandable && isExpanded && (
          <Box>
            {Array.isArray(value) ? (
              // Render array items
              value.map((item, index) =>
                renderValue(
                  `[${index}]`,
                  item,
                  itemLevel + 1
                )
              )
            ) : (
              // Render object properties
              Object.entries(value).map(([childKey, childValue]) =>
                renderValue(
                  childKey,
                  childValue,
                  itemLevel + 1
                )
              )
            )}
            <Box
              sx={{
                pl: itemLevel * 2,
                color: getValueColor(valueType),
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}
            >
              {valueType === 'array' ? ']' : '}'}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        color: '#1E293B',
        bgcolor: '#F8FAFC',
        p: 2,
        borderRadius: 1,
        overflow: 'auto'
      }}
    >
      {typeof data === 'object' && data !== null ? (
        Array.isArray(data) ? (
          data.map((item, index) =>
            renderValue(`[${index}]`, item, level)
          )
        ) : (
          Object.entries(data).map(([key, value]) =>
            renderValue(key, value, level)
          )
        )
      ) : (
        <Typography
          sx={{
            color: getValueColor(getValueType(data)),
            fontFamily: 'monospace'
          }}
        >
          {formatValue(data, getValueType(data))}
        </Typography>
      )}
    </Box>
  );
};

export default JsonTreeViewer;